import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Checkbox, theme as antdTheme } from 'antd';
import styled from 'styled-components';

import FormButton from '@components/form/form-button';
import { useFormStore } from '@store/use-form-store';

import AddAnotherIcon from '../../../assets/addAnother.svg';
import PersonalData from '../../../assets/PersonalData.svg';
import { ROUTES } from '../../../constants/routes';
import { useVismaConfiguration } from '../../../services/visitor-management';
import { useRegisterPage } from '../register-page-context';
import Registrant from './registrant';

const { screenSMMax, screenMDMax } = antdTheme.getDesignToken();

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const RegisteredVisitor = styled.h3`
  margin-left: 20px;
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};

  @media only screen and (max-width: ${screenMDMax}px) {
    font-size: 22px;
  }

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 18px;
    margin-left: 8px;
    margin-bottom: 4px;
  }
`;
const VisitorsContainer = styled.div`
  padding: 20px;
  height: 100%;

  @media only screen and (max-width: ${screenSMMax}px) {
    padding: 8px;
  }
`;

const NoRegisteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const NoRegisteredText = styled.span`
  font-size: 28px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};

  @media only screen and (max-width: ${screenMDMax}px) {
    font-size: 20px;
  }

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 16px;
  }
`;

const CheckboxText = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  & > * {
    flex: 1;
  }

  @media only screen and (max-width: ${screenMDMax}px) {
    flex-direction: column;
    gap: 8px;

    & > * {
      flex: unset;
      width: 100%;
    }
  }
`;

const ErrorText = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.error};
  margin-bottom: 8px;
`;

const RegistrantsContainer = styled.div`
  overflow: auto;
`;

const WelcomeBannerImg = styled.img`
  height: 100%;
  width: 100%;
`;

interface RegistrantListProps {
  handleAddRegistrant: () => void;
}

function RegistrantList({ handleAddRegistrant }: RegistrantListProps) {
  const intl = useIntl();
  const { setRegisteredPersons } = useFormStore();
  const navigate = useNavigate();
  const { registeredPersons } = useRegisterPage()!;
  const { configuration } = useVismaConfiguration();
  const multi = configuration?.personalData?.multi;
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState({
    status: false,
    errorMessage: ''
  });

  useEffect(() => {
    setError({
      status: false,
      errorMessage: ''
    });
  }, [registeredPersons]);

  const handleProcessStep = () => {
    if (!checked) {
      setError({
        status: true,
        errorMessage: intl.formatMessage({ id: 'PLEASE_CHECK_CHECKBOX' })
      });
      return;
    }
    if (registeredPersons.length < 2) {
      setError({
        status: true,
        errorMessage: intl.formatMessage({ id: 'REGISTRANT_BELOW_2_ERROR' })
      });
      return;
    }
    setRegisteredPersons(registeredPersons);
    navigate(ROUTES.VR_VIDEO);
  };

  return (
    <Container>
      <RegisteredVisitor>{multi?.visitor_list_title}</RegisteredVisitor>
      <VisitorsContainer>
        {registeredPersons.length > 0 ? (
          <>
            <RegistrantsContainer>
              {registeredPersons.map((registrant, index) => (
                <Registrant key={index} registrant={registrant} />
              ))}
            </RegistrantsContainer>
            {error.status && <ErrorText>{error.errorMessage}</ErrorText>}
            <ButtonContainer>
              <CheckboxContainer>
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                >
                  <CheckboxText>{multi?.visitor_list_accept}</CheckboxText>
                </Checkbox>
              </CheckboxContainer>
              <ButtonsGroup>
                <FormButton
                  type="primary"
                  icon={<img src={AddAnotherIcon} alt="add-another" />}
                  iconPosition="start"
                  disabled={registeredPersons.length >= 5}
                  onClick={handleAddRegistrant}
                >
                  {intl.formatMessage({ id: 'ADD_ANOTHER' })}
                </FormButton>
                <FormButton
                  type="primary"
                  withRightArrow
                  disabled={registeredPersons.length < 2 || !checked}
                  onClick={handleProcessStep}
                >
                  {multi?.button_video}
                </FormButton>
              </ButtonsGroup>
            </ButtonContainer>
          </>
        ) : (
          <NoRegisteredContainer>
            <WelcomeBannerImg src={PersonalData} alt="welcome-banner" />
            <NoRegisteredText>{multi?.visitor_list_empty}</NoRegisteredText>
          </NoRegisteredContainer>
        )}
      </VisitorsContainer>
    </Container>
  );
}

export default RegistrantList;
